(() => {
  const namespace = '__fajFeaturedCollection';

  /* The section can output this asset more than once. Reuse the existing
     controller so a form never receives duplicate submit listeners. */
  if (window[namespace]) {
    window[namespace].initAll(document);
    return;
  }

  const instances = new Map();

  class FajFeaturedCollection {
    constructor(section) {
      this.section = section;
      this.tabs = Array.from(section.querySelectorAll('[data-fajfc-tab]'));
      this.panels = Array.from(section.querySelectorAll('[data-fajfc-panel]'));
      this.liveRegion = section.querySelector('[data-fajfc-live-region]');
      this.galleryStates = new Map();
      this.formRequests = new Map();
      this.flickityWaitTimer = null;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onMediaLoad = this.onMediaLoad.bind(this);
      this.onResize = this.debounce(this.refreshVisibleGalleries.bind(this), 180);
    }

    init() {
      this.section.addEventListener('click', this.onClick);
      this.section.addEventListener('keydown', this.onKeydown);
      this.section.addEventListener('submit', this.onSubmit);
      this.section.addEventListener('load', this.onMediaLoad, true);
      window.addEventListener('resize', this.onResize);

      if (this.section.dataset.layout === 'slider') {
        this.waitForFlickity();
      }
    }

    getTracks(panel = null) {
      if (panel) return Array.from(panel.querySelectorAll('[data-fajfc-track]'));

      if (this.panels.length) {
        return this.panels
          .filter((item) => !item.hidden)
          .flatMap((item) => Array.from(item.querySelectorAll('[data-fajfc-track]')));
      }

      const legacyTrack = this.section.querySelector('[data-fajfc-track]');
      return legacyTrack ? [legacyTrack] : [];
    }

    waitForFlickity(attempt = 0) {
      if (typeof window.Flickity === 'function') {
        this.initVisibleGalleries();
        return;
      }

      if (attempt < 50) {
        this.flickityWaitTimer = window.setTimeout(() => this.waitForFlickity(attempt + 1), 100);
      }
      /* If Flickity never loads, the CSS grid fallback remains visible. */
    }

    initVisibleGalleries() {
      if (this.section.dataset.layout !== 'slider' || typeof window.Flickity !== 'function') return;
      this.getTracks().forEach((track) => this.initGallery(track));
    }

    initGallery(track) {
      if (!track || this.galleryStates.has(track) || this.section.dataset.layout !== 'slider') return;

      const cards = Array.from(track.querySelectorAll('[data-fajfc-card]'));
      if (!cards.length) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.resetCardHeights(cards);

      const flickity = new window.Flickity(track, {
        cellSelector: '[data-fajfc-card]',
        cellAlign: this.section.dataset.cellAlign || 'left',
        contain: true,
        wrapAround: this.section.dataset.wrapAround === 'true',
        pageDots: this.section.dataset.pageDots === 'true',
        prevNextButtons: this.section.dataset.arrows === 'true',
        groupCells: this.section.dataset.groupCells === 'true',
        adaptiveHeight: false,
        draggable: !reducedMotion,
        accessibility: true,
        selectedAttraction: reducedMotion ? 1 : 0.025,
        friction: reducedMotion ? 1 : 0.28
      });

      const state = { flickity, cards, frame: 0 };
      this.galleryStates.set(track, state);

      flickity.on('ready', () => this.scheduleEqualize(track));
      flickity.on('settle', () => this.scheduleEqualize(track, false));
      this.scheduleEqualize(track);

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.scheduleEqualize(track)).catch(() => {});
      }
    }

    resetCardHeights(cards) {
      cards.forEach((card) => {
        card.style.minHeight = '';
      });
    }

    scheduleEqualize(track, updateGallery = true) {
      const state = this.galleryStates.get(track);
      if (!state) return;

      window.cancelAnimationFrame(state.frame);
      state.frame = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => this.equalizeCardHeights(track, updateGallery));
      });
    }

    equalizeCardHeights(track, updateGallery = true) {
      const state = this.galleryStates.get(track);
      if (!state || track.closest('[data-fajfc-panel]')?.hidden) return;

      this.resetCardHeights(state.cards);

      const tallest = state.cards.reduce((height, card) => {
        return Math.max(height, Math.ceil(card.getBoundingClientRect().height), card.scrollHeight);
      }, 0);

      if (tallest > 1) {
        state.cards.forEach((card) => {
          card.style.minHeight = `${tallest}px`;
        });
      }

      if (updateGallery) {
        state.flickity.resize();
        state.flickity.reposition();
      }
    }

    refreshGallery(track) {
      const state = this.galleryStates.get(track);
      if (!state) {
        this.initGallery(track);
        return;
      }

      this.resetCardHeights(state.cards);
      state.flickity.resize();
      state.flickity.reposition();
      this.scheduleEqualize(track);
    }

    refreshVisibleGalleries() {
      if (this.section.dataset.layout !== 'slider') return;
      this.getTracks().forEach((track) => this.refreshGallery(track));
    }

    onClick(event) {
      const tab = event.target.closest('[data-fajfc-tab]');
      if (!tab || !this.section.contains(tab)) return;
      event.preventDefault();
      this.activatePanel(tab.dataset.panelId, true);
    }

    onKeydown(event) {
      const tab = event.target.closest('[data-fajfc-tab]');
      if (!tab || !this.tabs.length) return;

      const currentIndex = this.tabs.indexOf(tab);
      if (currentIndex < 0) return;

      let nextIndex = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;

      if (nextIndex === null) return;
      event.preventDefault();
      const nextTab = this.tabs[nextIndex];
      this.activatePanel(nextTab.dataset.panelId, true);
    }

    activatePanel(panelId, focusTab = false) {
      if (!panelId) return;
      const targetPanel = this.panels.find((panel) => panel.id === panelId);
      if (!targetPanel) return;

      this.tabs.forEach((tab) => {
        const active = tab.dataset.panelId === panelId;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        if (active && focusTab) tab.focus({ preventScroll: true });
      });

      this.panels.forEach((panel) => {
        const active = panel === targetPanel;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      window.requestAnimationFrame(() => {
        if (this.section.dataset.layout === 'slider') {
          this.getTracks(targetPanel).forEach((track) => this.refreshGallery(track));
        }
      });

      if (this.liveRegion) {
        const label = targetPanel.dataset.panelLabel || 'Collection';
        this.liveRegion.textContent = `${label} products shown.`;
      }
    }

    onMediaLoad(event) {
      if (!event.target || !event.target.matches('img')) return;
      const track = event.target.closest('[data-fajfc-track]');
      if (track && this.galleryStates.has(track)) this.scheduleEqualize(track);
    }

    async onSubmit(event) {
      const form = event.target.closest('[data-fajfc-product-form]');
      if (!form || this.section.dataset.ajaxCart !== 'true') return;

      event.preventDefault();
      event.stopPropagation();

      /* Ignore rapid clicks, Enter-key repeats, and programmatic re-submits
         until the current request for this exact product form is finished. */
      if (this.formRequests.has(form)) return;

      const requestController = new AbortController();
      this.formRequests.set(form, requestController);

      const button = form.querySelector('[data-fajfc-add-button]');
      const errorBox = form.querySelector('[data-fajfc-form-error]');
      const originalText = button?.querySelector('span')?.textContent || '';

      if (button) {
        button.disabled = true;
        const label = button.querySelector('span');
        if (label) label.textContent = 'Adding…';
      }
      if (errorBox) {
        errorBox.hidden = true;
        errorBox.textContent = '';
      }

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/json' },
          signal: requestController.signal
        });

        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload.description || payload.message || 'This product could not be added to the cart.');
        }

        if (this.liveRegion) {
          this.liveRegion.textContent = `${payload.product_title || 'Product'} added to cart.`;
        }

        if (button) {
          const label = button.querySelector('span');
          if (label) label.textContent = 'Added';
        }

        const shopRoot = window.Shopify?.routes?.root || '/';
        window.location.assign(`${shopRoot}cart`);
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (errorBox) {
          errorBox.textContent = error.message;
          errorBox.hidden = false;
        }
        if (this.liveRegion) this.liveRegion.textContent = error.message;
      } finally {
        window.setTimeout(() => {
          if (this.formRequests.get(form) !== requestController) return;
          this.formRequests.delete(form);

          if (button) {
            button.disabled = false;
            const label = button.querySelector('span');
            if (label) label.textContent = originalText;
          }
        }, 900);
      }
    }

    activateBlock(blockElement) {
      const panel = blockElement?.matches?.('[data-fajfc-panel]')
        ? blockElement
        : blockElement?.closest?.('[data-fajfc-panel]');
      if (panel) this.activatePanel(panel.id, false);
    }

    destroy() {
      this.formRequests.forEach((controller) => controller.abort());
      this.formRequests.clear();
      window.clearTimeout(this.flickityWaitTimer);
      this.section.removeEventListener('click', this.onClick);
      this.section.removeEventListener('keydown', this.onKeydown);
      this.section.removeEventListener('submit', this.onSubmit);
      this.section.removeEventListener('load', this.onMediaLoad, true);
      window.removeEventListener('resize', this.onResize);

      this.galleryStates.forEach((state) => {
        window.cancelAnimationFrame(state.frame);
        this.resetCardHeights(state.cards);
        state.flickity.destroy();
      });
      this.galleryStates.clear();
    }

    debounce(callback, wait) {
      let timeout;
      return (...args) => {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(() => callback(...args), wait);
      };
    }
  }

  const initSection = (section) => {
    if (!section || instances.has(section)) return;
    const instance = new FajFeaturedCollection(section);
    instances.set(section, instance);
    instance.init();
  };

  const destroySection = (section) => {
    const instance = instances.get(section);
    if (!instance) return;
    instance.destroy();
    instances.delete(section);
  };

  const initAll = (root = document) => {
    if (root.matches?.('[data-fajfc-section]')) initSection(root);
    root.querySelectorAll('[data-fajfc-section]').forEach(initSection);
  };

  window[namespace] = { initAll, destroySection };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-fajfc-section]').forEach(destroySection);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest?.('[data-fajfc-section]');
    const instance = section ? instances.get(section) : null;
    instance?.activateBlock(event.target);
  });
})();
